import { Map } from 'immutable'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import _forEach from 'lodash/forEach'
import _isString from 'lodash/isString'
import _split from 'lodash/split'
import _isNil from 'lodash/isNil'
import { getPrimary } from '../services/languages/index'
import { getLabel as getLangLabel } from '../services/lang/index'
import { EN } from '../services/languages/constants'
import { TYPE_FEATURE } from '../services/video/index'
import { get as getConfig } from '../config/index'
import { isSharePage } from '../services/url/index'
import { isIOS } from '../environment/index'
// Local storage prefix keys
export const LS_KEY_PREFIX_AUDIO = 'LS_AUDIO_'
export const LS_KEY_PREFIX_SUBTITLE = 'LS_SUBTITLE_'
const config = getConfig()
export const MIME_TYPE_VIDEO_PLAYER = 'application/x-mpegURL'

/**
 * Get the current video player time
 */
export function createCurrentTime (startPosition, player) {
  try {
    const duration = player.duration()
    if (startPosition < 60) {
      return 0
    }
    if (duration > 0 && startPosition > duration - 60) {
      return 0
    }
    return startPosition - 5
  } catch (e) {
    return 0
  }
}

export function setPosition (position, player) {
  try {
    player.currentTime(position)
  } catch (e) {
    // Do nothing
  }
}

export function getCurrentPosition (player) {
  try {
    return Math.floor(player.currentTime())
  } catch (e) {
    return 0
  }
}

/**
 * Create the Bright Cove VideoJs player options
 * @param {Object} options The options
 * @param {Map} options.user The user data
 * @param {String} options.poster A poster image url
 * @param {Boolean} options.controls When true display controls
 * @param {Boolean} options.loop When true loop video playback
 * @param {Boolean} options.isLive When true this is a live player
 * @param {Boolean} options.playsinline When true the video plays inline
 * @param {Number} options.inactivityTimeout The inactivity timeout in seconds
 */
export function getVideoOpts (options = {}) {
  const {
    user = Map(),
    poster = '',
    controls = true,
    loop = false,
    isLive = false,
    playsinline = false,
    inactivityTimeout = 3500,
  } = options
  const opts = {
    poster,
    loop,
    playsinline,
    liveui: isLive,
    controls,
    inactivityTimeout,
    language: user.getIn(['data', 'language', '0'], null),
  }

  if (enableFlashTech()) {
    opts.techOrder = ['flash', 'html5']
  }
  return opts
}

export function setupVideoPlayer (options = {}) {
  const {
    player,
    media,
    type,
    user,
    userVideo,
    location = null,
    src = null,
    text = null,
    startAt = null,
    isLiveVideo = false,
  } = options
  const mediaOptions = getMediaLangParams(user, media, location)

  const mediaSource = !_isNil(text) ? text : media.getIn(['data', 'textTracks'], Map())
  const audioSource = !_isNil(src) ? src : _get(mediaOptions, 'url', media.getIn(['data', 'bcHLS']))

  const mediaData = {
    media: mediaSource,
    src: audioSource,
  }

  const positionData = !isLiveVideo
    ? {
      type,
      userVideo,
      startAt,
    }
    : {
      type: null,
      userVideo: null,
      startAt: null,
    }

  setVideoPlayerData(player, mediaData, positionData, isLiveVideo)
  return player
}

export function setStartPosition (player, type, userVideo, startAt) {
  // An explicit startAt (e.g. from ?startAt= deep-link) always wins — skip resume
  // and clamping logic so the player seeks exactly where requested.
  if (startAt) {
    setPosition(startAt, player)
    // For HLS, seeking in loadedmetadata delays canplay which prevents autoplay.
    // Explicitly call play() once the seek is complete so the video autoplays
    // from the requested position regardless of how long HLS buffering takes.
    player.one('seeked', () => {
      player.play().catch(() => {})
    })
    return
  }
  let startPosition = 0
  if (type === TYPE_FEATURE) {
    startPosition = userVideo.getIn(['data', 'featurePosition']) || 0
  }
  setPosition(createCurrentTime(startPosition, player), player)
}

export function getPlayerLang (location, userLang) {
  if (location !== null) {
    const playerLang = _get(location.query, 'playerLang', null)
    if (playerLang) {
      return playerLang
    }
  }
  if (userLang && userLang.size) {
    return _get(_split(userLang.first(), '-'), 0, EN)
  }
  return config.appLang
}

export function getMediaLangParams (user, media, location = null) {
  // these have to be null bc that is there default state in the store
  // and immutable runs hasOwnProperty which returns the null as a valid value

  const { appLang } = config
  const userLang = user.getIn(['data', 'language'], null)
  const mediaLang = media.getIn(['data', 'byLang'], null)
  const mediaSubtitles = media.getIn(['data', 'textTracks', 'subtitles'], Map())
  const defaultSubtitles = mediaSubtitles.size > 0 ? mediaSubtitles.toJS() : undefined
  const defaultMediaUrl = media.getIn(['data', 'bcHLS'])
  const playerLang = getPlayerLang(location, userLang)
  const isDubbedMedia = (Map.isMap(mediaLang) && mediaLang.has(playerLang))
  let url = defaultMediaUrl
  let subtitles = null
  if (defaultSubtitles) {
    _forEach(defaultSubtitles, (value, key) => {
      if (key === getPrimary(userLang) && !isDubbedMedia) { subtitles = key }
    })
  }

  if (_get(config, ['features', 'player', 'detectLang'])) {
    url = (mediaLang && mediaLang.find((v, k) => k === playerLang)) || url
    if (defaultMediaUrl === url && playerLang !== appLang && defaultSubtitles && !isDubbedMedia) {
      subtitles = playerLang
    }
  }

  return { url, subtitles }
}

export function getContainerStyles (props) {
  const { auth } = props
  let availablewindowHeight = global.innerHeight
    || document.documentElement.clientHeight
    || document.body.clientHeight
  const windowWidth = global.innerWidth
    || document.documentElement.clientWidth
    || document.body.clientWidth
  const styles = {}
  const authenticated = _isString(auth.get('jwt'))

  // if not authenticated a email capture banner is added to bottom of player
  // recalcualte hieght
  if (!authenticated) {
    const emailCaptureBannerHeight = 35
    availablewindowHeight = (global.innerHeight
        || document.documentElement.clientHeight
        || document.body.clientHeight) - emailCaptureBannerHeight
    styles.emailCaptureBannerHeight = emailCaptureBannerHeight
  }

  styles.height = availablewindowHeight
  styles.width = availablewindowHeight / 0.5625
  // if the width of the container is greater than the width of the window
  // recalculate height and width
  if (styles.width > windowWidth) {
    styles.width = windowWidth
    styles.height = styles.width * 0.5625
  }
  return styles
}

export function enableFlashTech () {
  return browserIsIe()
}

function browserIsIe () {
  const userAgent = _get(global, 'navigator.userAgent', '')
  const msie = _includes(userAgent, 'MSIE ')
  if (msie) {
    // IE 10 or older => return version number
    return true
  }
  const trident = _includes(userAgent, 'Trident/')
  if (trident) {
    // IE 11 => return version number
    return true
  }
  const edge = _includes(userAgent, 'Edge/')
  if (edge) {
    // Edge (IE 12+) => return version number
    return true
  }
  return false
}

/** Add subtitle to the video player */
function setPlayerSubtitles (media, player, isLiveVideo) {
  // Add the video player text tracks
  try {
    player.addTextTrack('metadata', 'off', 'off')
  } catch (e) {
    // Do nothing
  }
  if (!Map.isMap(media)) {
    return
  }
  const captions = media.get('captions') || Map()
  captions.forEach((src, srcLang) => {
    try {
      const trackOptions = {
        src,
        kind: 'captions',
        label: getLangLabel(srcLang),
        srclang: srcLang,
      }
      player.addRemoteTextTrack(trackOptions, false)
    } catch (e) {
      // Do nothing
    }
  })
  const subtitles = media.get('subtitles') || Map()
  subtitles.forEach((src, srcLang) => {
    const remoteTrackOptions = {
      src,
      kind: 'subtitles',
      label: getLangLabel(srcLang),
      srclang: srcLang,
    }
    if (isSharePage && isLiveVideo === src) {
      remoteTrackOptions.default = true
      remoteTrackOptions.mode = 'showing'
    }
    try {
      player.addRemoteTextTrack(remoteTrackOptions, false)
    } catch (e) {
      // Do nothing
    }
  })
  const textThumbnail = media.get('thumbnail')
  if (textThumbnail && !isLiveVideo) {
    // add the thumbnails only once, when the video is playable
    try {
      const trackOptions = {
        src: textThumbnail,
        kind: 'metadata',
        label: 'thumbnails',
        mode: 'hidden',
      }
      player.addRemoteTextTrack(trackOptions, false)
    } catch (e) {
      // Do nothing
    }
  }
}

export function setVideoPlayerData (dataPlayer, mediaData, positionData, isLiveVideo = false) {
  const { media, src } = mediaData
  const { type = null, userVideo = null, startAt = null } = positionData
  dataPlayer.ready(function onPlayerReady () {
    const player = this
    const mediaId = Map.isMap(media) && media.getIn(['id'])
    if (mediaId) {
      // eslint-disable-next-line no-param-reassign
      player.mediainfo = {
        id: mediaId.toString(),
      }
    }
    player.src({
      src,
      type: MIME_TYPE_VIDEO_PLAYER,
    })

    setPlayerSubtitles(media, player, isLiveVideo)
    if (!isLiveVideo) {
      player.one(isIOS() ? 'loadeddata' : 'loadedmetadata', () => {
        setStartPosition(player, type, userVideo, startAt)
      })
    }
  })
}
